import {
  Activity,
  AlertCircle,
  Clock,
  FileJson,
  MessageSquare,
  RefreshCw,
  Upload,
  User,
} from "lucide-react";
import React, { type FC, useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import { formatDateOnly, RebootLiveModal, StatCard } from "./RebootLiveModal";

interface ParsedSession {
  playerName: string;
  movementType: string;
  selectedDate: string;
  messageCount: number;
  userMsgCount: number;
  aiMsgCount: number;
  hasReport: boolean;
  exportDate: string;
  version: string;
  fileSizeFormatted: string;
  sessionDuration: string;
  raw: Record<string, unknown>;
}

export interface ImportChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResumeInChat: (data: {
    messages: Array<{ role: string; content: unknown }>;
    reportContent?: string;
    playerName: string;
    movementType: string;
    selectedDate: string;
    sessionId?: string;
    containerId?: string;
    metadata?: Record<string, unknown>;
  }) => void;
}

type ModalPhase = "upload" | "parsing" | "preview" | "error";

export const ImportChatModal: FC<ImportChatModalProps> = ({
  isOpen,
  onClose,
  onResumeInChat,
}) => {
  const [phase, setPhase] = useState<ModalPhase>("upload");
  const [parsed, setParsed] = useState<ParsedSession | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    if (isOpen) {
      setPhase("upload");
      setParsed(null);
      setErrorMessage("");
      setFileName("");
    }
  }, [isOpen]);

  const parseFile = useCallback((file: File) => {
    if (!file.name.endsWith(".json")) {
      setErrorMessage(
        "Invalid file type. Please upload a .json file exported from Reboot.",
      );
      setPhase("error");
      return;
    }

    setFileName(file.name);
    setPhase("parsing");

    const reader = new FileReader();
    reader.onload = (e) => {
      const rawText =
        (e.target?.result as string)?.replace(/^\uFEFF/, "") ?? "";
      setTimeout(() => {
        try {
          const data = JSON.parse(rawText) as Record<string, unknown>;

          const hasSessionId = typeof data.sessionId === "string";
          const hasSession = data.session && typeof data.session === "object";
          const messages = Array.isArray(data.messages) ? data.messages : [];

          if (!hasSessionId && !hasSession && messages.length === 0) {
            setErrorMessage(
              "This file doesn't contain a valid Reboot session.",
            );
            setPhase("error");
            return;
          }

          const sessionObj = (hasSession ? data.session : {}) as Record<
            string,
            unknown
          >;
          const userMsgs = messages.filter(
            (m: { role?: string }) => m.role === "user",
          );
          const aiMsgs = messages.filter(
            (m: { role?: string }) => m.role === "assistant",
          );

          let duration = "< 1 min";
          if (messages.length >= 2) {
            const firstMsg = messages[0] as { timestamp?: string };
            const lastMsg = messages[messages.length - 1] as {
              timestamp?: string;
            };
            const first = firstMsg.timestamp
              ? new Date(firstMsg.timestamp).getTime()
              : 0;
            const last = lastMsg.timestamp
              ? new Date(lastMsg.timestamp).getTime()
              : 0;
            if (last > first) {
              const mins = Math.floor((last - first) / 60000);
              if (mins >= 1) duration = `${mins} min${mins > 1 ? "s" : ""}`;
            }
          }

          const bytes = new Blob([rawText]).size;
          const sizeFormatted =
            bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;

          const meta = (data.metadata || {}) as Record<string, unknown>;
          const session: ParsedSession = {
            playerName:
              (meta.playerName as string) ||
              (sessionObj.playerName as string) ||
              "Unknown Player",
            movementType:
              (meta.movementType as string) ||
              (sessionObj.movementType as string) ||
              "Unknown",
            selectedDate:
              (sessionObj.selectedDate as string) ||
              (meta.startDate ? String(meta.startDate) : "N/A"),
            messageCount: messages.length,
            userMsgCount: userMsgs.length,
            aiMsgCount: aiMsgs.length,
            hasReport: !!data.report,
            exportDate:
              (data.exportedAt as string) || (data.exportDate as string) || "",
            version: String(data.version ?? "—"),
            fileSizeFormatted: sizeFormatted,
            sessionDuration: duration,
            raw: data,
          };
          setParsed(session);
          setPhase("preview");
        } catch {
          setErrorMessage(
            "Failed to parse JSON. The file may be corrupted or improperly formatted.",
          );
          setPhase("error");
        }
      }, 400);
    };
    reader.onerror = () => {
      setErrorMessage("Could not read the file. Please try again.");
      setPhase("error");
    };
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDropAccepted: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) parseFile(file);
    },
    onDropRejected: (rejections) => {
      const msg = rejections[0]?.errors[0]?.message;
      setErrorMessage(
        msg ||
          "Invalid file type. Please upload a .json file exported from Reboot.",
      );
      setPhase("error");
    },
    accept: ".json",
    maxFiles: 1,
    multiple: false,
  });

  const handleResumeInChat = () => {
    if (!parsed) return;
    const raw = parsed.raw;
    onResumeInChat({
      messages:
        (raw.messages as Array<{ role: string; content: unknown }>) || [],
      reportContent: raw.report as string | undefined,
      playerName: parsed.playerName,
      movementType: parsed.movementType,
      selectedDate: parsed.selectedDate,
      sessionId: raw.sessionId as string | undefined,
      containerId: raw.containerId as string | undefined,
      metadata: raw.metadata as Record<string, unknown> | undefined,
    });
    onClose();
  };

  const handleReset = () => {
    setPhase("upload");
    setParsed(null);
    setErrorMessage("");
    setFileName("");
  };

  const subtitle =
    phase === "upload"
      ? "Upload a .json export to continue a previous conversation"
      : phase === "parsing"
        ? "Reading session data..."
        : phase === "preview"
          ? "Session loaded"
          : "Something went wrong";

  return (
    <RebootLiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Previous Conversation"
      subtitle={subtitle}
      dataTestId="import-chat-modal"
      icon={<Upload className="size-5 text-orange-500" />}
      canClose={phase !== "parsing"}
      maxWidth="md"
    >
      {/* Upload phase */}
      {phase === "upload" && (
        <div className="px-6 pb-6" data-testid="import-chat-upload-phase">
          <div
            {...getRootProps()}
            data-testid="import-chat-dropzone"
            className={`relative border-2 border-dashed rounded-lg flex flex-col items-center justify-center py-12 px-6 cursor-pointer transition-all duration-200 ${
              isDragActive
                ? "border-orange-500 bg-orange-500/5"
                : "border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.18)] hover:bg-[#252527]/20"
            }`}
          >
            <input {...getInputProps()} />
            <div
              className={`size-14 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                isDragActive ? "bg-orange-500/15" : "bg-[#252527]"
              }`}
            >
              <FileJson
                className={`size-6 transition-colors ${isDragActive ? "text-orange-500" : "text-[#7e7e81]"}`}
              />
            </div>
            <p className="text-[#b6b2b2] text-sm text-center">
              <span className="text-orange-500 font-semibold">
                Click to browse
              </span>{" "}
              or drag & drop
            </p>
            <p className="text-[#555] text-xs mt-2">
              Accepts .json files exported from Reboot
            </p>
          </div>
        </div>
      )}

      {/* Parsing phase */}
      {phase === "parsing" && (
        <div className="px-6 pb-8 pt-4 flex flex-col items-center gap-5">
          <div
            className="size-10 rounded-full border-2 border-[#252527] border-t-orange-500 animate-spin"
            aria-hidden
          />
          <div className="flex flex-col items-center gap-1">
            <p className="text-white text-sm font-medium">
              Parsing session file
            </p>
            <p className="text-[#7e7e81] text-xs">{fileName}</p>
          </div>
        </div>
      )}

      {/* Error phase */}
      {phase === "error" && (
        <div className="px-6 pb-6" data-testid="import-chat-error-phase">
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-5 flex flex-col items-center gap-3 text-center">
            <div className="size-10 rounded-full bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="size-5 text-red-400" />
            </div>
            <p
              className="text-red-300 text-[13px] leading-relaxed"
              data-testid="import-chat-error-message"
            >
              {errorMessage}
            </p>
          </div>
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={onClose}
              data-testid="import-chat-cancel-button"
              className="px-4 py-2 rounded-md text-[#7e7e81] hover:text-white hover:bg-[#252527] transition-colors text-[13px]"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              data-testid="import-chat-try-another-file-button"
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#252527] hover:bg-[#2f2f31] text-white transition-colors text-[13px] font-medium"
            >
              <RefreshCw className="size-3" />
              Try Another File
            </button>
          </div>
        </div>
      )}

      {/* Preview phase */}
      {phase === "preview" && parsed && (
        <div
          className="px-6 pb-6 flex flex-col gap-4"
          data-testid="import-chat-preview-phase"
        >
          <div className="relative flex items-center gap-3 bg-[#252527] rounded-lg px-3.5 py-3 overflow-hidden">
            <div className="size-9 rounded-md bg-orange-500/10 flex items-center justify-center shrink-0">
              <FileJson className="size-[18px] text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[13px] truncate">{fileName}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[#7e7e81] text-[11px]">
                  {parsed.fileSizeFormatted}
                </span>
                <span className="text-[#3a3a3c] text-[11px]">•</span>
                <span className="text-[#7e7e81] text-[11px]">
                  v{parsed.version}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <StatCard
              icon={<User className="size-3.5" />}
              value={parsed.playerName}
              label="Player"
              truncate
              dataTestId="import-preview-player"
            />
            <StatCard
              icon={<Activity className="size-3.5" />}
              value={parsed.movementType}
              label="Movement"
              truncate
              dataTestId="import-preview-movement"
            />
            <StatCard
              icon={<Clock className="size-3.5" />}
              value={
                parsed.selectedDate ? formatDateOnly(parsed.selectedDate) : "—"
              }
              label="Session Date"
              truncate
              dataTestId="import-preview-session-date"
            />
          </div>

          <div className="h-px bg-[rgba(255,255,255,0.06)]" />

          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              data-testid="import-chat-cancel-button"
              className="px-4 py-2 rounded-md text-[#7e7e81] hover:text-white hover:bg-[#252527] transition-colors text-sm"
            >
              Cancel
            </button>
            {parsed.messageCount > 0 && (
              <button
                onClick={handleResumeInChat}
                data-testid="import-chat-resume-button"
                className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-all bg-orange-500 text-white hover:bg-orange-600"
              >
                <MessageSquare className="size-3.5 text-white" />
                Resume Session
              </button>
            )}
          </div>
        </div>
      )}
    </RebootLiveModal>
  );
};
